import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    OvernightStayList,
    PointsOfInterestList,
    ProcessingRequestStatus,
    Route,
    RouteInstructionsAndMetrics,
    RouteMetadata,
    RouteSegmentPointsList,
    Trip,
    TripList,
    TripRequest,
} from "../generated/gpb/api/route";
import { createBaseQueryWithReauth, getAccessToken } from "../app/auth";

export interface ColumnSort
{
    desc: boolean;
    id: string;
}

export type SortingState = Array<ColumnSort>;

export type ColumnFiltersState = Array<ColumnFilter>;

export interface ColumnFilter
{
    id: string;
    value: unknown;
}

const TripsTag = "Trips";

export const RouteSegmentIteration = 3;

function getTripsQuery(
    index: number,
    count: number,
    sorting: SortingState,
    filter: ColumnFiltersState,
    searchTerm: string | undefined,
    archiveFilter: boolean | undefined
): string
{
    let url = `/trips?index=${index}&count=${count}`;
    if ( sorting.length > 0 ) url += `&sorting=${JSON.stringify( sorting )}`;
    if ( filter.length > 0 ) url += `&filter=${JSON.stringify( filter )}`;
    if ( searchTerm !== undefined ) url += `&searchTerm=${searchTerm}`;
    if ( archiveFilter !== undefined ) url += `&archive=${archiveFilter}`;
    return url;
}
export const routingApi = createApi( {
    reducerPath: "routingApi",
    baseQuery: createBaseQueryWithReauth(
        fetchBaseQuery( {
            baseUrl: `${process.env.REACT_APP_ROUTING_API_BASE}`,
            credentials: "same-origin",
            prepareHeaders: ( headers ) =>
            {
                headers.set( "Accept", "application/json" );
                headers.set( "Accept-Encoding", "gzip,deflate,br,zstd" );

                const accessToken = getAccessToken();
                if ( accessToken !== null )
                {
                    headers.set( "Authorization", `Bearer ${accessToken}` );
                }

                return headers;
            },
        } )
    ),
    tagTypes: [ TripsTag ],
    endpoints: ( builder ) => ( {
        getTrips: builder.query<TripList, {
            index: number,
            count: number,
            sorting: SortingState,
            filter: ColumnFiltersState,
            archiveFilter: boolean | undefined,
            searchTerm: string | undefined,
        }>( {
            query: ( { index, count, sorting, filter, archiveFilter, searchTerm } ) => ( {
                url: getTripsQuery( index, count, sorting, filter, searchTerm, archiveFilter ), // returns a TripList
                method: "GET",
            } ),
            providesTags: [ TripsTag ],
        } ),
        addTrip: builder.mutation<void, TripRequest>( {
            query: ( trip ) => ( {
                url: "/trips",
                method: "PUT",
                body: trip,
            } ),
            invalidatesTags: [ TripsTag ],
        } ),
        getTrip: builder.query<Trip, string>( {
            query: ( id ) => ( {
                url: `/trips/${id}`,
            } ),
        } ),
        getTripStatus: builder.query<ProcessingRequestStatus, string>( {
            query: ( id ) => ( {
                url: `/trips/status/${id}`,
                method: "POST",
            } ),
        } ),
        setSelectedRoute: builder.mutation<Trip, { tripId: string, routeId: string }>( {
            query: ( data ) => ( {
                url: `/trips/${data.tripId}/${data.routeId}`,
                method: "POST",
            } ),
            invalidatesTags: [ TripsTag ],
        } ),
        renameTrip: builder.mutation<Trip, { id: string, tripName: string }>( {
            query: ( data ) => ( {
                url: `/trips/${data.id}`,
                method: "POST",
                body: data.tripName
            } ),
            invalidatesTags: [ TripsTag ],
        } ),
        reassignTrip: builder.mutation<Trip, { tripId: string, userId: string }>( {
            query: ( data ) => ( {
                url: `/trips/reassign/${data.tripId}`,
                method: "POST",
                body: data.userId
            } ),
            invalidatesTags: [ TripsTag ],
        } ),
        deleteTrip: builder.mutation<Trip, string>( {
            query: ( id ) => ( {
                url: `/trips/${id}`,
                method: "DELETE",
            } ),
            invalidatesTags: [ TripsTag ],
        } ),
        restoreTrip: builder.mutation<Trip, string>( {
            query: ( id ) => ( {
                url: `/trips/restore/${id}`,
                method: "POST",
            } ),
            invalidatesTags: [ TripsTag ],
        } ),
        archiveTrip: builder.mutation<Trip, string>( {
            query: ( id ) => ( {
                url: `/trips/archive/${id}/true`,
                method: "POST",
            } ),
            invalidatesTags: [ TripsTag ],
        } ),
        unarchiveTrip: builder.mutation<Trip, string>( {
            query: ( id ) => ( {
                url: `/trips/archive/${id}/false`,
                method: "POST",
            } ),
            invalidatesTags: [ TripsTag ],
        } ),
        getRoute: builder.query<Route, string>( {
            query: ( id ) => ( {
                url: `/routes/${id}`,
            } ),
            transformResponse: Route.fromJSON,
        } ),
        getRouteMetadata: builder.query<RouteMetadata, string>( {
            query: ( id ) => ( {
                url: `/routes/meta/${id}`,
            } ),
            transformResponse: RouteMetadata.fromJSON,
        } ),
        getRouteSegmentPoints: builder.query<
        RouteSegmentPointsList,
        { id: string, index: number, count: number }
        >( {
            query: ( { id, index, count } ) => ( {
                url: `/routes/points/${id}?index=${index}&count=${count}`,
            } ),
            transformResponse: RouteSegmentPointsList.fromJSON,
        } ),
        getRouteMetricsAndInstructions: builder.query<
        RouteInstructionsAndMetrics,
        string
        >( {
            query: ( id: string ) => ( {
                url: `/routes/info/${id}`,
            } ),
            transformResponse: RouteInstructionsAndMetrics.fromJSON,
        } ),
        getPointsOfInterestList: builder.query<
        PointsOfInterestList,
        { routeId: string, index: number, count: number }
        >( {
            query: ( { routeId, index, count } ) => ( {
                url: `/POI/${routeId}?index=${index}&count=${count}`,
            } ),
            transformResponse: PointsOfInterestList.fromJSON,
        } ),
        getOvernightStayList: builder.query<
        OvernightStayList,
        { routeId: string }
        >( {
            query: ( { routeId, } ) => ( {
                url: `/overnights/${routeId}`,
            } ),
            transformResponse: OvernightStayList.fromJSON,
        } ),
    } ),
} );

export const {
    useGetTripsQuery,
    useRenameTripMutation,
    useAddTripMutation,
    useDeleteTripMutation,
    useReassignTripMutation,
    useGetTripQuery,
    useLazyGetTripQuery,
    useRestoreTripMutation,
    useGetRouteQuery,
    useLazyGetTripStatusQuery,
    useGetOvernightStayListQuery,
    useGetRouteMetadataQuery,
    useGetRouteSegmentPointsQuery,
    useGetRouteMetricsAndInstructionsQuery,
    useLazyGetPointsOfInterestListQuery,
    useSetSelectedRouteMutation,
    useArchiveTripMutation,
    useUnarchiveTripMutation,
} = routingApi;
